import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { WorkspaceFolder } from 'vscode-languageserver-protocol';
import { URI } from 'vscode-uri';
import { log } from '../../log';
import { DirectoryWalker, EmptyFileResolver, FileResolver } from '../fs';
import { FastDirectoryMatcher } from '../fs/dir_matcher';
import { parseURIString } from '../fs/utils';
import {
  DefaultVirtualFileSystemService,
  VirtualFileSystemEvents,
  VirtualFileSystemService,
  WorkspaceFilesUpdate,
  WorkspaceFileUpdate,
} from '../fs/virtual_file_system_service';
import { FsClient } from '../fs/fs';
import { GetFileOptions, Repository, RepositoryFile } from './repository';

type RepoUriString = string;
type FolderUriString = string;

type RepositoryMap = Map<RepoUriString, Repository>;

export interface RepositoryService extends DefaultRepositoryService {}

export const RepositoryService = createInterfaceId<RepositoryService>('RepositoryService');

/**
 * RepositoryService is responsible for managing and detecting repositories in a workspace.
 * This is considered the SSOT (Single Source of Truth) for repository state in a workspace.
 * TODO: Migrate `DuoProjectAccessCache` and `DuoProjectAccessService` to this service.
 *
 * @see `Repository` - Stateful class representing a git repository.
 * @see `GitIgnoreManager` - Manages gitignore files for a repository.
 */
@Injectable(RepositoryService, [VirtualFileSystemService, FileResolver, DirectoryWalker, FsClient])
export class DefaultRepositoryService {
  #virtualFileSystemService: DefaultVirtualFileSystemService;

  #fileResolver: EmptyFileResolver;

  #directoryWalker: DirectoryWalker;

  #fsClient: FsClient;

  /**
   * map of workspace folders to a directory matcher
   * The directory matcher is used to match repository URIs against file URIs
   * to determine if the file belongs to a repository.
   */
  #workspaceToRepositories: Map<
    FolderUriString,
    { fastDirectoryMatcher: FastDirectoryMatcher; repositories: RepositoryMap }
  >;

  constructor(
    virtualFileSystemService: DefaultVirtualFileSystemService,
    fileResolver: EmptyFileResolver,
    directoryWalker: DirectoryWalker,
    fsClient: FsClient,
  ) {
    this.#virtualFileSystemService = virtualFileSystemService;
    this.#fileResolver = fileResolver;
    this.#directoryWalker = directoryWalker;
    this.#fsClient = fsClient;
    this.#workspaceToRepositories = new Map();
    this.#setupEventListeners();
  }

  #setupEventListeners() {
    return this.#virtualFileSystemService.onFileSystemEvent(async (eventType, data) => {
      switch (eventType) {
        case VirtualFileSystemEvents.WorkspaceFilesEvent:
          await this.#setWorkspaceRepositories(data as WorkspaceFilesUpdate);
          break;
        case VirtualFileSystemEvents.WorkspaceFileEvent:
          await this.#handleWorkspaceFileUpdate(data as WorkspaceFileUpdate);
          break;
        default:
          break;
      }
    });
  }

  /**
   * ------------------------------
   * EVENT HANDLERS
   * ------------------------------
   */

  /**
   * Clear existing repositories from workspace and detect new repositories.
   * This can either be triggered by a workspace Files event by the VFS or by a git config file change (.git/config or .gitignore)
   */
  async #setWorkspaceRepositories({
    workspaceFolder,
    files: workspaceFiles,
  }: WorkspaceFilesUpdate) {
    // TODO: send LSP notification to client indicating progress
    // https://gitlab.com/gitlab-org/gitlab/-/issues/489467

    // first clear existing repositories from workspace
    log.info(`[RepositoryService] Clearing repositories for workspace ${workspaceFolder.uri}`);
    this.#clearRepositoriesForWorkspace(workspaceFolder.uri);

    const repositories = this.#detectRepositories(workspaceFiles, workspaceFolder);
    log.info(
      `[RepositoryService] Detected ${repositories.length} repositories for workspace ${workspaceFolder.uri}`,
    );

    for (const repository of repositories) {
      this.#addRepositoryToWorkspace(workspaceFolder, repository);
    }
    log.info(
      `[RepositoryService] Added ${repositories.length} repositories to workspace ${workspaceFolder.uri}`,
    );

    await this.#addFilesToRepositories(workspaceFiles, workspaceFolder);
  }

  async #handleWorkspaceFileUpdate({ fileUri, workspaceFolder, event }: WorkspaceFileUpdate) {
    // we don't support building repositories from file events
    // this is handled by the workspace files event
    const repository = this.getMatchingRepository(fileUri, workspaceFolder.uri);
    if (!repository) {
      log.debug(`[RepositoryService] No matching repository found for file ${fileUri.toString()}`);
      return;
    }

    if (!repository) {
      log.debug(`[RepositoryService] Repository not found for URI ${fileUri.toString()}`);
      return;
    }

    // if the file is a git config or gitignore file, we treat this as a workspace state change
    // this way we re-detect repositories and add/update files in them, as any git change
    // marks the current state as stale
    if (Repository.isGitConfigFile(fileUri) || Repository.isGitIgnoreFile(fileUri)) {
      log.info(
        `[RepositoryService] File ${fileUri.toString()} is a git config or gitignore file, updating workspace files`,
      );
      const filesForWorkspace = await this.#directoryWalker.findFilesForDirectory({
        directoryUri: parseURIString(workspaceFolder.uri),
      });
      log.info(
        `[RepositoryService] Found ${filesForWorkspace.length} files for workspace ${workspaceFolder.uri}`,
      );
      await this.#setWorkspaceRepositories({ workspaceFolder, files: filesForWorkspace });
      return;
    }

    // if the file change is in a git folder, we need to refresh the tracked files
    if (Repository.isInGitFolder(fileUri)) {
      await repository.refreshTrackedFiles();
    }

    if (repository.isFileIgnored(fileUri)) {
      log.debug(`[RepositoryService] File ${fileUri.toString()} is ignored`);
      return;
    }
    switch (event) {
      case 'add':
        repository.setFile(fileUri);
        log.debug(
          `[RepositoryService] File ${fileUri.toString()} added to repository ${repository.uri.toString()}`,
        );
        break;
      case 'change':
        repository.setFile(fileUri);
        log.debug(
          `[RepositoryService] File ${fileUri.toString()} updated in repository ${repository.uri.toString()}`,
        );
        break;
      case 'unlink':
        repository.removeFile(fileUri);
        log.debug(
          `[RepositoryService] File ${fileUri.toString()} removed from repository ${repository.uri.toString()}`,
        );
        break;
      default:
        log.warn(`[RepositoryService] Unknown file event ${event} for file ${fileUri.toString()}`);
    }
  }

  #getFilesByRepositories(
    files: URI[],
    workspaceFolder: WorkspaceFolder,
  ): Map<RepoUriString, Map<string, URI>> {
    const filesByRepositories = new Map<RepoUriString, Map<string, URI>>();

    for (const file of files) {
      const repoUri = this.#getRepositoryUriForFile(file, workspaceFolder.uri);
      if (repoUri) {
        const repoUriString = repoUri.toString();
        const repoFiles = filesByRepositories.get(repoUriString) ?? new Map();
        repoFiles.set(file.toString(), file);
        filesByRepositories.set(repoUriString, repoFiles);
      }
    }

    return filesByRepositories;
  }

  /**
   * ------------------------------
   * CLIENT FACING METHODS
   * ------------------------------
   */
  getMatchingRepository(fileUri: URI, workspaceFolderUri: FolderUriString): Repository | undefined {
    const repositoryUri = this.#getRepositoryUriForFile(fileUri, workspaceFolderUri);
    if (!repositoryUri) {
      return undefined;
    }
    const repository = this.getRepositoriesForWorkspace(workspaceFolderUri).get(
      repositoryUri.toString(),
    );
    return repository;
  }

  getRepositoryFileForUri(
    fileUri: URI,
    repositoryUri: URI,
    workspaceFolder: WorkspaceFolder,
  ): RepositoryFile | null {
    const repository = this.getRepositoryForWorkspace(workspaceFolder.uri, repositoryUri);
    return repository?.getFile(fileUri) ?? null;
  }

  getCurrentFilesForRepository(
    repositoryUri: URI,
    workspaceFolderUri: FolderUriString,
    options: GetFileOptions = {},
  ): RepositoryFile[] {
    const repository = this.getRepositoryForWorkspace(workspaceFolderUri, repositoryUri);
    if (!repository) {
      return [];
    }
    return repository.getCurrentTreeFiles(options);
  }

  getCurrentFilesForWorkspace(
    workspaceFolderUri: FolderUriString,
    options: GetFileOptions = {},
  ): RepositoryFile[] {
    const repositories = this.getRepositoriesForWorkspace(workspaceFolderUri);
    const allFiles: RepositoryFile[] = [];
    repositories.forEach((repository) => {
      repository.getCurrentTreeFiles(options).forEach((file) => {
        allFiles.push(file);
      });
    });
    return allFiles;
  }

  getRepositoriesForWorkspace(workspaceFolderUri: FolderUriString): RepositoryMap {
    return this.#workspaceToRepositories.get(workspaceFolderUri)?.repositories || new Map();
  }

  getRepositoryForWorkspace(
    workspaceFolderUri: FolderUriString,
    repositoryUri: URI,
  ): Repository | undefined {
    return this.getRepositoriesForWorkspace(workspaceFolderUri).get(repositoryUri.toString());
  }

  /**
   * ------------------------------
   * STATE MANAGEMENT
   * ------------------------------
   */
  #addRepositoryToWorkspace(workspaceFolder: WorkspaceFolder, repository: Repository) {
    const { fastDirectoryMatcher, repositories } = this.#workspaceToRepositories.get(
      workspaceFolder.uri,
    ) || {
      fastDirectoryMatcher: new FastDirectoryMatcher(),
      repositories: new Map(),
    };
    log.info(
      `[RepositoryService] Adding repository ${repository.uri} to workspace ${workspaceFolder.uri}`,
    );
    fastDirectoryMatcher.addDirectoryToMatch(repository.uri);
    repositories.set(repository.uri.toString(), repository);
    this.#workspaceToRepositories.set(workspaceFolder.uri, {
      fastDirectoryMatcher,
      repositories,
    });
  }

  async #addFilesToRepositories(files: URI[], workspaceFolder: WorkspaceFolder) {
    const repositoryMap = this.getRepositoriesForWorkspace(workspaceFolder.uri);
    const filesByRepositories = this.#getFilesByRepositories(files, workspaceFolder);

    const setupAndAddFiles = async ([repositoryUri, repoFiles]: [
      RepoUriString,
      Map<string, URI>,
    ]) => {
      const repository = repositoryMap.get(repositoryUri);
      if (!repository) return 0;
      const repoFileArray = Array.from(repoFiles.values());
      const fileCount = repoFileArray.length;

      const gitFiles = await repository.setupGitForRepository(repoFileArray);
      if (gitFiles instanceof Error) {
        log.warn(
          `[RepositoryService] Error setting up git for repository ${repository.uri.toString()}: ${gitFiles.message}`,
          gitFiles,
        );
        return 0;
      }
      log.debug(
        `[RepositoryService] Added ${gitFiles.ignoreFiles.size} gitignores to repository ${repository.uri}`,
      );
      log.debug(
        `[RepositoryService] Added ${gitFiles.trackedFiles.size} tracked files to repository ${repository.uri}`,
      );
      if (gitFiles.excludeFile) {
        log.debug(
          `[RepositoryService] Added ${gitFiles.excludeFile.toString()} to repository ${repository.uri}`,
        );
      }

      repoFileArray.forEach((repoFile) => repository.setFile(repoFile));

      log.debug(`[RepositoryService] Added ${fileCount} files to repository ${repository.uri}`);

      return fileCount;
    };

    const results = await Promise.all(Array.from(filesByRepositories).map(setupAndAddFiles));
    const totalFiles = results.reduce((sum, count) => sum + count, 0);

    log.debug(`[RepositoryService] Total files added: ${totalFiles}`);
  }

  #getRepositoryUriForFile(fileUri: URI, workspaceFolderUri: FolderUriString): URI | undefined {
    const directoryMatcher = this.#getDirectoryMatcherForWorkspace(workspaceFolderUri);
    const repositoryUris = directoryMatcher.findMatchingDirectories(fileUri);
    if (!repositoryUris?.length) {
      log.warn(`[RepositoryService] No matching repository found for file ${fileUri.toString()}`);
      return undefined;
    }
    // the directory matcher returns the directories in order from shallow to deep
    // so we always return the deepest repository uri
    return repositoryUris.at(-1);
  }

  #getDirectoryMatcherForWorkspace(workspaceFolderUri: FolderUriString): FastDirectoryMatcher {
    return (
      this.#workspaceToRepositories.get(workspaceFolderUri)?.fastDirectoryMatcher ||
      new FastDirectoryMatcher()
    );
  }

  #clearRepositoriesForWorkspace(workspaceFolderUri: FolderUriString): void {
    log.debug(`[RepositoryService] Emptying repositories for workspace ${workspaceFolderUri}`);
    const directoryMatcher = this.#getDirectoryMatcherForWorkspace(workspaceFolderUri);
    directoryMatcher.dispose();
    this.#workspaceToRepositories.delete(workspaceFolderUri);
    const repositoryMap = this.getRepositoriesForWorkspace(workspaceFolderUri);
    repositoryMap.forEach((repo) => {
      repo.dispose();
      log.debug(`[RepositoryService] Repository ${repo.uri} has been disposed`);
    });
    repositoryMap.clear();
    this.#workspaceToRepositories.delete(workspaceFolderUri);
    log.debug(
      `[RepositoryService] Repositories for workspace ${workspaceFolderUri} have been emptied`,
    );
  }

  /**
   *------------------------------
   * GIT RELATED OPERATIONS
   * ------------------------------
   */

  /**
   * Detects repositories in the workspace by finding .git/config files.
   * For each .git/config file, it creates a repository object.
   * It also reads the gitignore files and adds them to the repository's ignore manager.
   */
  #detectRepositories(files: URI[], workspaceFolder: WorkspaceFolder): Repository[] {
    const gitConfigFiles = files.filter((file) => Repository.isGitConfigFile(file));
    return gitConfigFiles.map((file) => {
      log.info(`[RepositoryService] Detected repository at ${file.toString()}`);
      return new Repository({
        fileResolver: this.#fileResolver,
        uri: Repository.getRepoRootFromGitConfig(file),
        configFileUri: file,
        workspaceFolder,
        fsClient: this.#fsClient,
      });
    });
  }
}
